<template>
  <div>
    <v-dialog v-model="dialog" eager persistent scrollable max-width="560">
      <v-form ref="FeedbackForm" @submit.prevent>
        <v-card class="feedback-card" elevation="0">
          <!-- Header -->
          <div class="dialog-header">
            <div class="header-left">
              <div class="header-icon-wrap">
                <v-icon size="18" color="white"
                  >mdi-message-text-outline</v-icon
                >
              </div>
              <div>
                <div class="header-title text-white">{{ action }} Feedback</div>
              </div>
            </div>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              class="close-btn"
              @click="closeD()"
            />
          </div>

          <v-divider class="header-divider" />

          <v-card-text class="dialog-body">
            <!-- Step 1: Feedback type -->
            <div class="step-block">
              <div class="step-label">Feedback type</div>
              <div class="type-row">
                <div
                  class="type-tile"
                  :class="{ active: feedback_type === 'general' }"
                  @click="feedback_type = 'general'"
                >
                  <div class="tile-icon-wrap">
                    <v-icon size="22">mdi-lightbulb-outline</v-icon>
                  </div>
                  <div class="tile-text">
                    <span class="tile-name">Comment</span>
                    <span class="tile-desc">General observation</span>
                  </div>
                  <transition name="check-pop">
                    <div class="tile-check" v-if="feedback_type === 'general'">
                      <v-icon size="12" color="white">mdi-check</v-icon>
                    </div>
                  </transition>
                </div>

                <div
                  class="type-tile"
                  :class="{ active: feedback_type === 'concern' }"
                  @click="feedback_type = 'concern'"
                >
                  <div class="tile-icon-wrap">
                    <v-icon size="22">mdi-account-search-outline</v-icon>
                  </div>
                  <div class="tile-text">
                    <span class="tile-name">Concern</span>
                    <span class="tile-desc">Issue needing attention</span>
                  </div>
                  <transition name="check-pop">
                    <div class="tile-check" v-if="feedback_type === 'concern'">
                      <v-icon size="12" color="white">mdi-check</v-icon>
                    </div>
                  </transition>
                </div>
              </div>
              <transition name="err-drop">
                <div class="type-error" v-if="showTypeError">
                  <v-icon size="13" color="#ef4444"
                    >mdi-alert-circle-outline</v-icon
                  >
                  Please select a feedback type to continue.
                </div>
              </transition>
            </div>

            <!-- Step 2 & 3 shown after type selected -->
            <transition name="fade-slide">
              <div v-if="feedback_type">
                <!-- Step 2: Select Intern -->
                <div class="step-block">
                  <div class="step-label">Intern/s Concerned</div>
                  <div
                    class="intern-list py-2 rounded-lg"
                    style="
                      overflow-y: auto;
                      border: 1px solid #e5e7eb;
                      max-height: 150px;
                    "
                  >
                    <div
                      class="intern-pill"
                      v-for="(intern, idx) in intern_selected"
                      :key="idx"
                    >
                      <div class="intern-avatar">{{ getInitials(intern) }}</div>
                      <span class="intern-name">{{ intern }}</span>
                    </div>
                    <div
                      class="intern-empty"
                      v-if="intern_selected.length === 0"
                    >
                      <v-icon size="14" color="#9ca3af"
                        >mdi-account-off-outline</v-icon
                      >
                      No interns selected
                    </div>
                  </div>
                </div>

                <!-- Step 3: Feedback text -->
                <div class="step-block">
                  <div class="step-label">Feedback</div>
                  <div class="textarea-wrap">
                    <v-textarea
                      placeholder="Describe your feedback in detail…"
                      :rules="[formRules.required]"
                      :readonly="action == 'View'"
                      v-model="feedback"
                      variant="outlined"
                      density="compact"
                      rows="4"
                      no-resize
                      :color="$vuetify.theme.themes.light.inputColor"
                      class="custom-ta"
                      hide-details="auto"
                      counter="500"
                      maxlength="500"
                    />
                  </div>
                </div>

                <div v-if="tab != 1">
                  <div class="step-label" v-if="feedback_type == 'concern'">
                    Monitor Date
                  </div>
                  <div class="textarea-wrap">
                    <v-text-field
                      hide-details="auto"
                      :model-value="formatDate(monitoring_date)"
                      variant="outlined"
                      density="compact"
                      :readonly="tab != 1"
                      :rules="[formRules.required]"
                      @click="showDatePicker = true"
                    ></v-text-field>

                    <v-dialog v-model="showDatePicker" max-width="290">
                      <v-date-picker
                        v-model="monitoring_date"
                        @update:model-value="saveMonitoringDate"
                        :color="$vuetify.theme.themes.light.inputColor"
                      ></v-date-picker>
                    </v-dialog>
                  </div>
                </div>

                <!-- monitoring status -->
                <div
                  class="step-block mt-4"
                  v-if="tab == 2 && feedback_type == 'concern'"
                >
                  <div class="step-label">Monitoring Status</div>
                  <div class="type-row">
                    <div
                      class="type-tile"
                      :class="{ active: monitoring_status === 2 }"
                      @click="setMonitoringStatus(2)"
                    >
                      <div class="tile-icon-wrap">
                        <v-icon size="22">mdi-lightbulb-outline</v-icon>
                      </div>
                      <div class="tile-text">
                        <span class="tile-name">Completed</span>
                        <span class="tile-desc">Monitoring Completed</span>
                      </div>
                      <transition name="check-pop">
                        <div class="tile-check" v-if="monitoring_status === 2">
                          <v-icon size="12" color="white">mdi-check</v-icon>
                        </div>
                      </transition>
                    </div>

                    <div
                      class="type-tile"
                      :class="{ active: monitoring_status === 3 }"
                      @click="setMonitoringStatus(3)"
                    >
                      <div class="tile-icon-wrap">
                        <v-icon size="22">mdi-account-search-outline</v-icon>
                      </div>
                      <div class="tile-text">
                        <span class="tile-name">Follow-up Needed</span>
                        <span class="tile-desc"
                          >Follow-up concern to agency</span
                        >
                      </div>
                      <transition name="check-pop">
                        <div class="tile-check" v-if="monitoring_status === 3">
                          <v-icon size="12" color="white">mdi-check</v-icon>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>

                <!-- Monitoring panels with slide animation -->
                <div
                  v-if="
                    tab == 2 && feedback_type == 'concern' && showMonitoring
                  "
                  class="monitoring-slide-host"
                >
                  <transition :name="monitoringSlideTransition" mode="out-in">
                    <!-- Completed panel -->
                    <div v-if="monitoring_status == 2" key="completed">
                      <div
                        class="step-block last"
                        v-if="feedback_type == 'concern'"
                      >
                        <div class="step-label">Actions Taken</div>
                        <div class="textarea-wrap">
                          <v-textarea
                            :readonly="tab == 3"
                            placeholder="Enter details here…"
                            :rules="
                              tab == 2 && feedback_type == 'concern'
                                ? [formRules.required]
                                : []
                            "
                            v-model="resolution"
                            variant="outlined"
                            density="compact"
                            rows="4"
                            no-resize
                            :color="$vuetify.theme.themes.light.inputColor"
                            class="custom-ta"
                            hide-details="auto"
                            counter="500"
                            maxlength="500"
                          />
                        </div>
                      </div>

                      <div
                        class="step-block last"
                        v-if="tab != 1 && feedback_type == 'concern'"
                      >
                        <div class="step-label">Date Resolved</div>
                        <div class="textarea-wrap">
                          <v-text-field
                            hide-details="auto"
                            :model-value="formatDate(date_resolved)"
                            variant="outlined"
                            density="compact"
                            :readonly="tab == 3"
                            :rules="[formRules.required]"
                            @click="showDatePicker = true"
                          ></v-text-field>

                          <v-dialog v-model="showDatePicker" max-width="290">
                            <v-date-picker
                              v-model="date_resolved"
                              @update:model-value="saveDate"
                              :color="$vuetify.theme.themes.light.inputColor"
                            ></v-date-picker>
                          </v-dialog>
                        </div>
                      </div>
                    </div>

                    <!-- Follow-up panel -->
                    <div v-else-if="monitoring_status == 3" key="followup">
                      <div
                        class="step-block last"
                        v-if="feedback_type == 'concern'"
                      >
                        <div class="step-label">Follow-up Notes</div>
                        <div class="textarea-wrap">
                          <v-textarea
                            :readonly="tab == 3"
                            placeholder="Enter details here…"
                            :rules="
                              tab == 2 && feedback_type == 'concern'
                                ? [formRules.required]
                                : []
                            "
                            v-model="follow_up_note"
                            variant="outlined"
                            density="compact"
                            rows="4"
                            no-resize
                            :color="$vuetify.theme.themes.light.inputColor"
                            class="custom-ta"
                            hide-details="auto"
                            counter="500"
                            maxlength="500"
                          />
                        </div>
                      </div>

                      <div
                        class="step-block last"
                        v-if="feedback_type == 'concern'"
                      >
                        <div class="step-label">Follow-up Date</div>
                        <div class="textarea-wrap">
                          <v-text-field
                            hide-details="auto"
                            :model-value="formatDate(follow_up_date)"
                            variant="outlined"
                            density="compact"
                            :readonly="tab == 3"
                            :rules="[formRules.required]"
                            @click="showDatePicker = true"
                          ></v-text-field>

                          <v-dialog v-model="showDatePicker" max-width="290">
                            <v-date-picker
                              v-model="follow_up_date"
                              @update:model-value="saveFollowupDate"
                              :color="$vuetify.theme.themes.light.inputColor"
                            ></v-date-picker>
                          </v-dialog>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </transition>
          </v-card-text>

          <v-divider />

          <!-- Actions -->
          <v-card-actions class="dialog-actions">
            <v-btn class="cancel-btn" variant="text" @click="closeD()">
              <v-icon size="16" class="mr-1">mdi-close</v-icon>
              Close
            </v-btn>
            <v-btn
              v-if="tab == 1"
              class="submit-btn"
              variant="flat"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="acknowledge()"
            >
              <v-icon size="16" class="mr-1">mdi-check-circle-outline</v-icon>
              Acknowledge
            </v-btn>
            <v-btn
              v-if="tab == 2 && feedback_type == 'concern'"
              class="submit-btn"
              variant="flat"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="save()"
            >
              <v-icon size="16" class="mr-1">mdi-check-circle-outline</v-icon>
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";
export default {
  props: {
    data: Object,
    action: String,
    tab: Number,
  },
  data() {
    return {
      dialog: false,
      feedback_type: null,
      feedback: null,
      btnLoading: false,
      intern_selected: [],
      internlist: [],
      resolution: null,
      date_resolved: null,
      follow_up_date: null,
      monitoring_date: null,
      showTypeError: false,
      monitoring_status: null,
      monitoring_status_list: [],
      showDatePicker: false,
      showDatePicker1: false,
      follow_up_note: null,
      showMonitoring: false,
      // tracks previous status to determine slide direction
      prevMonitoringStatus: null,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
    };
  },
  computed: {
    // 2 = Completed (left), 3 = Follow-up Needed (right)
    // clicking Completed from Follow-up → slide right-to-left (slide-left)
    // clicking Follow-up from Completed → slide left-to-right (slide-right)
    monitoringSlideTransition() {
      if (this.prevMonitoringStatus === null) return "mon-fade";
      return this.monitoring_status > this.prevMonitoringStatus
        ? "mon-slide-right"
        : "mon-slide-left";
    },
    getStudentHandled() {
      let handled = [];
      if (this.data) {
        if (this.data.handled_interns[0].length > 0) {
          for (let i = 0; i < this.data.handled_interns[0].length; i++) {
            handled.push(this.data.handled_interns[0][i].student.id);
          }
        }
      }
      return handled;
    },
    getIsFeedbackByFieldOfficer() {
      return this.$store.state.user.user.usertypeID == 9;
    },
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        this.showTypeError = false;
        this.prevMonitoringStatus = null;
        this.loadInterns();
        if (data.id) {
          this.feedback_type = data.feedback_type;
          this.feedback = data.feedback;
          this.resolution = data.resolution;
          this.follow_up_note = data.follow_up_note;
          this.follow_up_date = data.follow_up_date;
          this.monitoring_status = data.monitoring_status;
          this.showMonitoring = this.tab != 1 && data.monitoring_status != 1;
          this.date_resolved = data.date_resolved;
          this.resolution = data.resolution;
          this.monitoring_date = data.monitoring_date;
          for (let i = 0; i < data.interns.length; i++) {
            let format_ = `${data.interns[i].student.fname}  ${data.interns[i].student.lname} (${data.interns[i].student.program.abbr})`;
            this.intern_selected.push(format_);
          }
        }
      },
      deep: true,
    },
    feedback_type(val) {
      if (val) this.showTypeError = false;
    },
  },
  methods: {
    setMonitoringStatus(status) {
      this.prevMonitoringStatus = this.monitoring_status;
      this.monitoring_status = status;
      this.showMonitoring = true;
    },

    saveDate() {
      if (this.date_resolved) {
        this.date_resolved = this.formatDate3(this.date_resolved);
        this.showDatePicker = false;
      }
    },
    saveMonitoringDate() {
      if (this.monitoring_date) {
        this.monitoring_date = this.formatDate3(this.monitoring_date);
        this.showDatePicker = false;
      }
    },
    saveFollowupDate() {
      if (this.follow_up_date) {
        this.follow_up_date = this.formatDate3(this.follow_up_date);
        this.showDatePicker1 = false;
      }
    },
    getInitials(name) {
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((n) => n[0].toUpperCase())
        .join("");
    },
    loadInterns() {
      this.axiosCall("/organization/getMyInterns", "GET").then((res) => {
        this.internlist = res.data;
      });
    },
    async acknowledge() {
      if (!this.feedback_type) {
        this.showTypeError = true;
        return;
      }
      this.btnLoading = true;
      await this.$nextTick();
      const validate = await this.$refs.FeedbackForm.validate();
      if (validate.valid) {
        this.axiosCall(
          "/organization-intern-feedback/acknowledge/" + this.data.id,
          "PATCH",
          {
            monitoring_date:
              this.feedback_type == "concern" ? this.monitoring_date : null,
          },
        ).then((res) => {
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.closeD();
          } else if (res.data.status == 400) {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      } else {
        this.btnLoading = false;
      }
    },
    async save() {
      this.btnLoading = true;
      const validate = await this.$refs.FeedbackForm.validate();
      if (validate.valid) {
        let data = {
          resolution: this.resolution,
          date_resolved:
            this.monitoring_status == 2 ? this.date_resolved : null,
          monitoring_status: this.monitoring_status,
          follow_up_date:
            this.monitoring_status == 3 ? this.date_resolved : null,
        };
        this.axiosCall(
          "/organization-intern-feedback/resolve/" + this.data.id,
          "PATCH",
          data,
        ).then((res) => {
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.closeD();
          } else if (res.data.status == 400) {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      } else {
        this.btnLoading = false;
      }
    },
    closeD() {
      eventBus.emit("closeFeedbackDialog", true);
      this.btnLoading = false;
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
/* ── Card ── */
.feedback-card {
  border-radius: 16px !important;
  overflow: hidden;
  font-family: "DM Sans", sans-serif;
}

/* ── Header ── */
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: #fff;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #16a34a, #22c55e);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.35);
}
.header-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.2px;
}
.header-divider {
  opacity: 0.06;
}
.close-btn {
  color: #9ca3af !important;
}
.close-btn:hover {
  color: #374151 !important;
  background: #f3f4f6 !important;
}

/* ── Body ── */
.dialog-body {
  padding: 20px 24px !important;
  background: #f9fafb;
}

/* ── Steps ── */
.step-block {
  margin-bottom: 20px;
}
.step-block.last {
  margin-bottom: 0;
}
.step-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

/* ── Type tiles ── */
.type-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.type-tile {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 13px;
  border-radius: 14px;
  border: 1.5px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  user-select: none;
}
.type-tile:hover {
  border-color: #86efac;
  background: #f0fdf4;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(22, 163, 74, 0.1);
}
.type-tile.active {
  border-color: #16a34a;
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15),
    0 4px 14px rgba(22, 163, 74, 0.1);
}
.tile-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.2s ease;
  flex-shrink: 0;
}
.type-tile.active .tile-icon-wrap {
  background: linear-gradient(135deg, #16a34a, #22c55e);
  color: white;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.35);
}
.tile-text {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.tile-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.tile-desc {
  font-size: 10.5px;
  color: #9ca3af;
  margin-top: 2px;
  line-height: 1.3;
}
.tile-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.4);
}

/* ── Type error ── */
.type-error {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: #ef4444;
  margin-top: 8px;
  padding: 7px 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

/* ── Intern pills ── */
.intern-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.intern-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 11px 5px 5px;
  background: white;
  border: 1.5px solid #d1fae5;
  border-radius: 30px;
  box-shadow: 0 1px 4px rgba(22, 163, 74, 0.08);
}
.intern-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #16a34a, #4ade80);
  color: white;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.intern-name {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}
.intern-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  padding: 6px 0;
}

/* ── Textarea ── */
.textarea-wrap {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.custom-ta :deep(.v-field) {
  border-radius: 12px !important;
  background: #ffffff !important;
  font-size: 13.5px !important;
  font-family: "DM Sans", sans-serif !important;
}
.custom-ta :deep(.v-field--focused) {
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.18) !important;
}

/* ── Actions ── */
.dialog-actions {
  padding: 14px 20px !important;
  background: #fff;
  gap: 8px;
  justify-content: flex-end;
}
.cancel-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
}
.cancel-btn:hover {
  background: #f3f4f6 !important;
  color: #374151 !important;
}
.submit-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #16a34a, #22c55e) !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  padding: 0 18px !important;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3) !important;
  transition: all 0.18s ease !important;
}
.submit-btn:hover {
  box-shadow: 0 6px 16px rgba(22, 163, 74, 0.4) !important;
  transform: translateY(-1px);
}
.submit-btn:disabled {
  opacity: 0.6 !important;
}

/* ── Monitoring slide host ── */
.monitoring-slide-host {
  overflow: hidden;
}

/* ── Transitions ── */

/* Initial fade-only (first selection, no previous) */
.mon-fade-enter-active,
.mon-fade-leave-active {
  transition: opacity 0.25s ease;
}
.mon-fade-enter-from,
.mon-fade-leave-to {
  opacity: 0;
}

/* Slide right: Completed → Follow-up (new panel comes from right) */
.mon-slide-right-enter-active,
.mon-slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.mon-slide-right-enter-from {
  opacity: 0;
  transform: translateX(48px);
}
.mon-slide-right-leave-to {
  opacity: 0;
  transform: translateX(-48px);
}

/* Slide left: Follow-up → Completed (new panel comes from left) */
.mon-slide-left-enter-active,
.mon-slide-left-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.mon-slide-left-enter-from {
  opacity: 0;
  transform: translateX(-48px);
}
.mon-slide-left-leave-to {
  opacity: 0;
  transform: translateX(48px);
}

.fade-slide-enter-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
.check-pop-enter-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.check-pop-enter-from {
  opacity: 0;
  transform: scale(0.4);
}
.err-drop-enter-active {
  transition: all 0.2s ease;
}
.err-drop-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
